import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  NotificationDto,
  NotificationPageOptions,
  notificationService,
} from "@/services/notification.service";

interface PaginatedPage {
  data: NotificationDto[];
  meta: {
    page: number;
    limit: number;
    hasNextPage?: boolean;
    [key: string]: unknown;
  };
}

export const useNotifications = (
  params?: NotificationPageOptions,
  enabled = true,
) => {
  const limit = params?.limit || 20;

  return useInfiniteQuery({
    queryKey: ["notifications", limit],
    queryFn: async ({ pageParam = 1 }) => {
      return notificationService.getNotifications({
        ...params,
        page: pageParam,
        limit,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.meta?.hasNextPage) return lastPage.meta.page + 1;
      if (lastPage.data?.length === limit) return allPages.length + 1;
      return undefined;
    },
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled,
  });
};

export const useUnreadNotificationCount = (enabled = true) => {
  return useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: () => notificationService.getUnreadCount(),
    staleTime: 1 * 60 * 1000,
    enabled,
  });
};

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onMutate: (id: string) => {
      // Optimistically update is_read in-place without reordering
      // Use a predicate to only target InfiniteQuery caches (they have "pages")
      queryClient.setQueriesData<InfiniteData<PaginatedPage>>(
        {
          queryKey: ["notifications"],
          predicate: (query) =>
            query.state.data != null &&
            typeof query.state.data === "object" &&
            "pages" in (query.state.data as object),
        },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((n) =>
                n.id === id ? { ...n, is_read: true } : n,
              ),
            })),
          };
        },
      );
    },
    onSuccess: () => {
      // Only refresh the unread count badge
      queryClient.invalidateQueries({
        queryKey: ["notifications", "unread-count"],
      });
    },
  });
};

export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onMutate: () => {
      // Mark all notifications as read in-place
      queryClient.setQueriesData<InfiniteData<PaginatedPage>>(
        {
          queryKey: ["notifications"],
          predicate: (query) =>
            query.state.data != null &&
            typeof query.state.data === "object" &&
            "pages" in (query.state.data as object),
        },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((n) => ({ ...n, is_read: true })),
            })),
          };
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications", "unread-count"],
      });
    },
  });
};
